import React, { FC, ReactNode } from 'react';

// ---------------------------------------------------------------------------
// Button
// ---------------------------------------------------------------------------
// Unified button component that covers both button shapes used in the app:
//
//   shape="pill"    — wizard nav/action buttons (secondary + modifier classes)
//   shape="rounded" — Add/Install/action buttons in table headers and cards
//
// Variants map to the following visual treatments:
//
//   primary   → blue fill  (brand --primary colour, brand-aware)
//   submit    → green fill (rgba(153,187,63), same as Add buttons)
//   outline   → white fill, themed border + text  (Back buttons)
//   tertiary  → dark/black fill  (Scan, Advanced Scan)
//   danger    → red fill  (#da4441)
//   disabled  → grey fill  (unclickable state, applied automatically when disabled=true)
//
// Shape defaults:
//   pill    → py-1 px-5   (wizard standard)
//   rounded → p-2         (table-header Add/Install standard)
//
// Toggle buttons (active prop):
//   Pass `active` to make the button behave as a two-state toggle. When
//   active=true the specified `variant` is used; when active=false the button
//   renders as `primary` (brand blue — the inactive/paused state).
//   Override padding via `className` (e.g. className="py-2 px-4").
//
// Usage examples:
//   <Button variant="outline"   shape="pill"    onClick={onBack}>Back</Button>
//   <Button variant="primary"   shape="pill"    onClick={onNext}>Next</Button>
//   <Button variant="submit"    shape="pill"    type="submit">Install</Button>
//   <Button variant="tertiary"  shape="pill"    onClick={scan}>Scan Subnet</Button>
//   <Button variant="danger"    shape="pill"    onClick={del}>Delete</Button>
//
//   <Button variant="submit"    shape="rounded" onClick={onAdd}>Add</Button>
//   <Button variant="primary"   shape="rounded" onClick={onInstall}>Install</Button>
//   <Button variant="danger"    shape="rounded" onClick={onRemove}>Remove</Button>
//
//   <Button variant="submit"    shape="rounded" active={isPlaying} onClick={toggle}>Play/Pause</Button>
//   <Button variant="primary"   shape="rounded" disabled={!hasSelection} className="py-2 px-4" onClick={ack}>Acknowledge</Button>
//
// The `invisible` prop renders a zero-opacity placeholder preserving footer
// layout on the first wizard step where Back should not appear.
// ---------------------------------------------------------------------------

export type ButtonVariant = 'primary' | 'submit' | 'outline' | 'tertiary' | 'danger';
export type ButtonShape = 'pill' | 'rounded';

interface ButtonProps {
    variant: ButtonVariant;
    shape?: ButtonShape;
    children: ReactNode;
    type?: 'button' | 'submit' | 'reset';
    disabled?: boolean;
    /**
     * Toggle mode. When provided, renders as `variant` when true and as
     * `primary` (brand blue) when false. Omit for non-toggle buttons.
     */
    active?: boolean;
    /** Renders as visibility:hidden — preserves layout space without showing the button */
    invisible?: boolean;
    onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    className?: string;
    id?: string;
    'data-cy'?: string;
}

// Pill shape: uses the secondary + modifier CSS classes from App.css.
// Rounded shape: uses Tailwind directly — consistent with existing Add/Install buttons.

const PILL_VARIANT_CLASSES: Record<ButtonVariant, string> = {
    primary:  'secondary secondary-enabled',
    submit:   'secondary secondary-submit',
    outline:  'secondary secondary-inverse',
    tertiary: 'secondary secondary-tertiary',
    danger:   'secondary secondary-danger',
};

const ROUNDED_VARIANT_CLASSES: Record<ButtonVariant, string> = {
    primary:  'flex rounded-lg bg-primary p-2 shadow text-white text-sm items-center',
    submit:   'flex rounded-lg bg-lime p-2 shadow text-white text-sm items-center',
    outline:  'flex rounded-lg bg-white p-2 border-2 border-primary text-primary font-bold text-sm items-center',
    tertiary: 'flex rounded-lg bg-sidemenu-focus p-2 shadow text-white text-sm items-center',
    danger:   'flex rounded-lg bg-red-danger p-2 shadow text-white text-sm items-center',
};

const PILL_SIZE = 'py-1 px-5';

const DISABLED_PILL    = 'secondary secondary-disabled cursor-not-allowed';
const DISABLED_ROUNDED = 'flex rounded-lg bg-gray-400 p-2 shadow text-white text-sm items-center cursor-not-allowed';

export const Button: FC<ButtonProps> = ({
    variant,
    shape = 'pill',
    children,
    type = 'button',
    disabled = false,
    active,
    invisible = false,
    onClick,
    className = '',
    id,
    'data-cy': dataCy,
}) => {
    const isPill = shape === 'pill';

    // Resolve effective variant: toggle mode uses `primary` when inactive
    const effectiveVariant: ButtonVariant = (active === false) ? 'primary' : variant;

    const variantClass = disabled
        ? (isPill ? DISABLED_PILL : DISABLED_ROUNDED)
        : (isPill ? PILL_VARIANT_CLASSES[effectiveVariant] : ROUNDED_VARIANT_CLASSES[effectiveVariant]);

    const sizeClass = isPill ? PILL_SIZE : '';

    const visibilityClass = invisible ? 'invisible' : '';

    return (
        <button
            id={id}
            type={type}
            disabled={disabled}
            onClick={onClick}
            data-cy={dataCy}
            className={`${variantClass} ${sizeClass} ${visibilityClass} ${className}`.trim()}
        >
            {children}
        </button>
    );
};

export default Button;
